<script lang="ts">
  import { onDestroy } from 'svelte';
  import {
    BUILDING_COST_GROWTH, BUILDING_LATE_COST_GROWTH, BUILDING_LATE_CURVE_LEVEL, RESOURCE_BY_SKILL,
    MAX_BUILDING_LEVEL, MAX_TOOL_TIER, SKILL_BY_RESOURCE, SKILLS, TOOL_COST_GROWTH,
    buildingUpgradeRecipe, canAffordResources, resourceSellValue, toolUpgradeCost,
    toolUpgradeRequirements,
    type EconomyActionResult, type GameState, type PrestigeActionResult, type SkillId
  } from '@rsvjelle/game-core';
  import { formatNumber, SKILL_ICONS, titleCase } from '../lib/display.ts';

  interface Props {
    gameState: GameState;
    onSell: (resource: string, quantity: number) => EconomyActionResult;
    onBuyTool: () => EconomyActionResult;
    onBuyBuilding: () => EconomyActionResult;
    onAssignPassiveSkills: (skills: SkillId[]) => PrestigeActionResult;
    onSelectSkill: (skill: SkillId) => void;
  }
  let { gameState, onSell, onBuyTool, onBuyBuilding, onAssignPassiveSkills, onSelectSkill }: Props = $props();
  let active = $derived(gameState.run.skills[gameState.run.activeSkill]);
  let resource = $derived(RESOURCE_BY_SKILL[active.id]);
  let available = $derived(gameState.run.resources[resource] ?? 0);
  let sellValue = $derived(resourceSellValue(gameState));
  let toolCost = $derived(toolUpgradeCost(gameState));
  let toolRequirements = $derived(toolUpgradeRequirements(gameState));
  let buildingRecipe = $derived(buildingUpgradeRecipe(gameState));
  let canBuyBuilding = $derived(canAffordResources(gameState, buildingRecipe));
  let canBuyTool = $derived(
    gameState.run.gold >= toolCost
    && toolRequirements.meetsSkillLevel
    && toolRequirements.meetsHallLevel
    && !toolRequirements.atMaxTier
  );
  let buildingAtMax = $derived(active.buildingLevel >= MAX_BUILDING_LEVEL);
  let twinEchoRank = $derived(gameState.prestige.upgrades.twinEchoes ?? 0);
  let firstEcho = $state<SkillId>('Construction');
  let secondEcho = $state<SkillId>('Firemaking');
  let echoSelectionInitialized = $state(false);
  let feedback = $state('');
  let goldBursts = $state<Array<{ id: number; text: string }>>([]);
  let goldBurstId = 0;
  let sellTimer: ReturnType<typeof setTimeout> | null = null;
  let holdStartedAt = 0;
  let holdBatch = $state(25);

  $effect(() => {
    if (!echoSelectionInitialized && twinEchoRank > 0) {
      firstEcho = gameState.prestige.passiveSkills?.[0] ?? 'Construction';
      secondEcho = gameState.prestige.passiveSkills?.[1] ?? 'Firemaking';
      echoSelectionInitialized = true;
    }
  });

  function act(action: () => EconomyActionResult, success: string) {
    const result = action();
    feedback = result.ok ? success : (result.reason ?? 'That action is not available.');
    setTimeout(() => { feedback = ''; }, 2200);
  }

  function assignEchoes(skills: SkillId[], success: string) {
    const result = onAssignPassiveSkills(skills);
    feedback = result.ok ? success : (result.reason ?? 'Those Echoes cannot be assigned.');
    setTimeout(() => { feedback = ''; }, 2200);
  }

  function supportCurrentHall() {
    const support = Object.keys(buildingRecipe).map((name) => SKILL_BY_RESOURCE[name]);
    [firstEcho, secondEcho] = support;
    assignEchoes(support, `Twin Echoes now support the ${active.id} Hall.`);
  }

  function showGold(gainedGold: number): void {
    const id = ++goldBurstId;
    goldBursts = [...goldBursts, { id, text: `+${formatNumber(gainedGold)} gold` }].slice(-6);
    setTimeout(() => { goldBursts = goldBursts.filter((burst) => burst.id !== id); }, 850);
  }

  function sellOnce(quantity: number): boolean {
    const result = onSell(resource, quantity);
    if (!result.ok) {
      feedback = result.reason ?? 'Nothing available to sell.';
      setTimeout(() => { feedback = ''; }, 1800);
      return false;
    }
    showGold(result.gainedGold ?? 0);
    return true;
  }

  function sellFraction(fraction: number): void {
    sellOnce(available * fraction);
  }

  function stopSelling(): void {
    if (sellTimer) clearTimeout(sellTimer);
    sellTimer = null;
    holdBatch = 25;
  }

  function repeatSale(): void {
    if (available < 25 || !sellOnce(holdBatch)) { stopSelling(); return; }
    const heldFor = Date.now() - holdStartedAt;
    const delay = Math.max(55, 220 - heldFor / 18);
    const batchStage = Math.floor(heldFor / 3_000);
    holdBatch = Math.min(1_000_000_000, 25 * 2 ** batchStage);
    sellTimer = setTimeout(repeatSale, delay);
  }

  function startSelling(event: PointerEvent): void {
    if (available < 25 || sellTimer) return;
    event.preventDefault();
    (event.currentTarget as HTMLButtonElement).setPointerCapture(event.pointerId);
    holdStartedAt = Date.now();
    holdBatch = 25;
    sellOnce(25);
    sellTimer = setTimeout(repeatSale, 420);
  }

  onDestroy(stopSelling);
</script>

<section class="view-stack" aria-labelledby="upgrade-title">
  <div class="section-heading"><div><p class="eyebrow">WORKSHOP</p><h2 id="upgrade-title">Forge greater power</h2></div></div>
  <div class="feature-banner upgrade-skill-picker"><span>{SKILL_ICONS[active.id]}</span><div><small>Showing upgrades for</small><strong>{active.id}</strong></div><label><span>Switch skill</span><select value={active.id} onchange={(event) => onSelectSkill(event.currentTarget.value as SkillId)}>{#each SKILLS as skill}<option value={skill}>{skill} · Lv {gameState.run.skills[skill].level}</option>{/each}</select></label><b>Lv {active.level}</b></div>

  {#if !buildingAtMax}
    <article class="workshop-support-strip">
      <div><p class="eyebrow">REQUIRED FOR NEXT HALL</p><small>Switch and gather without leaving the Workshop</small></div>
      {#each Object.entries(buildingRecipe) as [supportResource, amount]}
        {@const supportSkill = SKILL_BY_RESOURCE[supportResource]}
        <button type="button" onclick={() => onSelectSkill(supportSkill)}>
          <span>{SKILL_ICONS[supportSkill]}</span><strong>{supportSkill}</strong>
          <small>{titleCase(supportResource)} {formatNumber(gameState.run.resources[supportResource] ?? 0)} / {formatNumber(amount)}</small>
        </button>
      {/each}
    </article>
  {/if}

  <article class="exchange-card">
    <div><p class="eyebrow">ROYAL EXCHANGE</p><h3>Trade {titleCase(resource)}</h3><small>{formatNumber(1 / sellValue, 1)} resources become 1 gold. Earned run value is never reduced.</small></div>
    <div class="exchange-balance"><span>◆ {formatNumber(available)} available</span><b>·</b><strong>● {formatNumber(gameState.run.gold)} owned</strong></div>
    <div class="exchange-actions">
      <div class="exchange-percentages">
        <button type="button" disabled={available <= 0} onclick={() => sellFraction(0.1)}><strong>Sell 10%</strong><small>+{formatNumber(available * 0.1 * sellValue)}</small></button>
        <button type="button" disabled={available <= 0} onclick={() => sellFraction(0.25)}><strong>Sell 25%</strong><small>+{formatNumber(available * 0.25 * sellValue)}</small></button>
        <button type="button" disabled={available <= 0} onclick={() => sellFraction(0.5)}><strong>Sell 50%</strong><small>+{formatNumber(available * 0.5 * sellValue)}</small></button>
      </div>
      <div class="exchange-primary-actions">
        <button class="hold-sale" type="button" disabled={available < 25} onpointerdown={startSelling} onpointerup={stopSelling} onpointercancel={stopSelling} onlostpointercapture={stopSelling} oncontextmenu={(event) => event.preventDefault()} onclick={(event) => { if (event.detail === 0) sellOnce(25); }}><span>Hold to sell</span><strong>{formatNumber(holdBatch, 0)} <i>→</i> +{formatNumber(holdBatch * sellValue)}</strong></button>
        <button class="sell-all" type="button" disabled={available < 1} onclick={() => sellOnce(available)}><span>Sell everything</span><strong>+{formatNumber(available * sellValue)} gold</strong></button>
      </div>
      {#each goldBursts as burst (burst.id)}<span class="gold-burst">{burst.text}</span>{/each}
    </div>
  </article>

  {#if feedback}<div class="action-feedback" role="status">{feedback}</div>{/if}

  <div class="upgrade-grid">
    <article class="upgrade-card featured">
      <div class="upgrade-icon">🛠️</div><p class="eyebrow">EQUIPMENT</p><h3>Masterwork Tool</h3>
      <p>Each tier adds +50% base XP and resource production. New tiers unlock every five {active.id} levels and may lead the Hall by at most five.</p>
      <div class="upgrade-level"><span>Tier</span><strong>{Math.min(active.toolTier, MAX_TOOL_TIER)}/{MAX_TOOL_TIER}</strong></div>
      <button class="purchase-button" type="button" disabled={!canBuyTool} onclick={() => act(onBuyTool, `${active.id} tool upgraded!`)}><span>{toolRequirements.atMaxTier ? 'Maximum tier reached' : `● ${formatNumber(toolCost)} gold`}</span><small>{toolRequirements.atMaxTier ? 'MAX' : !toolRequirements.meetsSkillLevel ? `Requires ${active.id} level ${toolRequirements.skillLevel}` : !toolRequirements.meetsHallLevel ? `Requires ${active.id} Hall ${toolRequirements.hallLevel}` : `Tier ${toolRequirements.nextTier} · next costs ×${TOOL_COST_GROWTH}`}</small></button>
    </article>
    <article class="upgrade-card">
      <div class="upgrade-icon">🏛️</div><p class="eyebrow">REALM BUILDING</p><h3>{active.id} Hall</h3>
      <p>Each level multiplies XP and resource production by ×1.05. Its mixed recipe requires support from two other skills.</p>
      <div class="upgrade-level"><span>Level</span><strong>{Math.min(active.buildingLevel, MAX_BUILDING_LEVEL)}/{MAX_BUILDING_LEVEL}</strong></div>
      <button class="purchase-button" type="button" disabled={buildingAtMax || !canBuyBuilding} onclick={() => act(onBuyBuilding, `${active.id} Hall upgraded!`)}><span>{buildingAtMax ? 'Maximum Hall reached' : Object.entries(buildingRecipe).map(([name, amount]) => `${formatNumber(amount)} ${titleCase(name)}`).join(' + ')}</span><small>{buildingAtMax ? 'MAX' : `Next costs ×${active.buildingLevel >= BUILDING_LATE_CURVE_LEVEL ? BUILDING_LATE_COST_GROWTH : BUILDING_COST_GROWTH}`}</small></button>
    </article>
    <article class:locked={twinEchoRank < 1} class="upgrade-card">
      <div class="upgrade-icon">👥</div><p class="eyebrow">ECHO WORKFORCE</p><h3>Twin Echoes</h3>
      {#if twinEchoRank < 1}
        <p>After five Ascensions, unlock two passive workers that gather support resources without earning XP.</p>
        <div class="lock-note">Unlock Twin Echoes in Ascension</div>
      {:else}
        <p>Two chosen skills gather resources at {twinEchoRank === 1 ? '12' : twinEchoRank === 2 ? '18' : '25'}% speed. They gain no passive XP.</p>
        <div class="echo-selectors">
          <label>Echo I<select bind:value={firstEcho}>{#each SKILLS as skill}<option value={skill}>{skill}</option>{/each}</select></label>
          <label>Echo II<select bind:value={secondEcho}>{#each SKILLS as skill}<option value={skill}>{skill}</option>{/each}</select></label>
        </div>
        <div class="echo-actions">
          <button class="purchase-button" type="button" disabled={firstEcho === secondEcho} onclick={() => assignEchoes([firstEcho, secondEcho], 'Twin Echoes reassigned.')}>Apply choices</button>
          <button class="echo-goal" type="button" onclick={supportCurrentHall}>Support this Hall</button>
        </div>
      {/if}
    </article>
  </div>
</section>
